import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/app.materials';
import { FormsModule } from '@angular/forms';
import { HouseViewsService } from '../../shared/services/house-views.service';
import { PortfolioService } from '../../pages/portfolio/portfolio.service';
import { NotificationService } from '../../shared/services/notification.service';
import { HouseViewsReadonlyComponent } from '../house-views-readonly/house-views-readonly.component';
import { ViewsSetterComponent } from '../views-setter/views-setter.component';
import { EffectiveViewsComponent } from '../effective-views/effective-views.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-optimization-views-panel',
  templateUrl: './optimization-views-panel.component.html',
  styleUrls: ['./optimization-views-panel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FormsModule,
    HouseViewsReadonlyComponent,
    ViewsSetterComponent,
    EffectiveViewsComponent,
  ],
})
export class OptimizationViewsPanelComponent implements OnInit {
  @Input() portfolio: any;
  @Input() optimizationData: any;
  @Input() constraintLists: any;

  @Output() optimize = new EventEmitter<void>();

  firmViews: any[] = [];
  userViews: any[] = [];

  firmEnabled: Record<string, boolean> = {};
  userEnabled: Record<string, boolean> = {};

  effectivePayload: any[] = [];
  payloadLoading = false;
  payloadError = '';

  frequencyConfirmedThisSession = false;

  constructor(
    private houseViewsService: HouseViewsService,
    private portfolioService: PortfolioService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    forkJoin({
      firm: this.houseViewsService.getFirmViews(),
      user: this.houseViewsService.getUserViews(),
    }).subscribe((resp: any) => {
      this.firmViews = resp.firm || [];
      this.userViews = resp.user || [];

      this.firmViews.forEach(v => {
        this.firmEnabled[this.getViewKey(v)] = true;
      });
      this.userViews.forEach(v => {
        this.userEnabled[this.getViewKey(v)] = true;
      });

      this.cdr.detectChanges();
    });
  }

  getViewKey(v: any): string {
    const typeId = v.type?.id || v.type;
    const itemId = v.items?.[0]?.id || 'unknown';
    // Relative views: {type}_{itemId}_vs_{sorted_items2_ids_joined_by_hyphen}
    if (v.items2 && v.items2.length) {
      const sortedItems2 = v.items2.map((i: any) => +i.id).sort((a, b) => a - b).join('-');
      return `${typeId}_${itemId}_vs_${sortedItems2}`;
    }
    return `${typeId}_${itemId}`;
  }

  onFirmMasterToggle(enabled: boolean): void {
    this.firmEnabled = {};
    this.firmViews.forEach(v => {
      this.firmEnabled[this.getViewKey(v)] = enabled;
    });
    this.firmEnabled = { ...this.firmEnabled };
  }

  onUserMasterToggle(enabled: boolean): void {
    this.userEnabled = {};
    this.userViews.forEach(v => {
      this.userEnabled[this.getViewKey(v)] = enabled;
    });
    this.userEnabled = { ...this.userEnabled };
  }

  onFirmViewToggle(event: { viewKey: string; enabled: boolean }): void {
    this.firmEnabled = { ...this.firmEnabled, [event.viewKey]: event.enabled };
  }

  onUserViewToggle(event: { viewKey: string; enabled: boolean }): void {
    this.userEnabled = { ...this.userEnabled, [event.viewKey]: event.enabled };
  }

  resolveItemName(itemId: number, type: string): string {
    // Look up in constraintLists by type
    const listMap: Record<string, string> = {
      asset: 'assets',
      sector: 'sectors',
      country: 'countries',
      index: 'benchmarks',
      currency: 'benchmarks',
    };
    const listKey = listMap[type];
    if (listKey && this.constraintLists?.[listKey]) {
      const list = this.constraintLists[listKey];
      // Benchmarks have a nested structure (groups with items)
      if (type === 'index' || type === 'currency') {
        for (const group of list) {
          if (group.items) {
            const found = group.items.find((i: any) => i.id === itemId);
            if (found) return found.name_full || found.name || found.index_name_full || String(itemId);
          }
          if (group.id === itemId) return group.name_full || group.name || String(itemId);
        }
      } else {
        const found = list.find((i: any) => +i.id === itemId);
        if (found) return found.name || found.company_name || String(itemId);
      }
    }

    // Fall back to searching firm/user views items
    const allViews = [...this.firmViews, ...this.userViews, ...(this.optimizationData?.views || [])];
    for (const v of allViews) {
      const vType = v.type?.id || v.type;
      if (vType === type && v.items) {
        const found = v.items.find((i: any) => +i.id === itemId);
        if (found) return found.name || String(itemId);
      }
    }

    return String(itemId);
  }

  generatePayload(): void {
    this.payloadLoading = true;
    this.payloadError = '';
    this.effectivePayload = [];

    const toggles: Record<string, string> = {};

    // Only include toggles for views that have been explicitly toggled off or
    // where the user wants a specific level to win over the default priority
    this.firmViews.forEach(v => {
      const key = this.getViewKey(v);
      if (this.firmEnabled[key] !== false) {
        toggles[key] = 'firm';
      }
    });

    // User views override firm for same key
    this.userViews.forEach(v => {
      const key = this.getViewKey(v);
      if (this.userEnabled[key] !== false) {
        toggles[key] = 'user';
      }
    });

    // Send empty body when no toggles (default priority applies), otherwise include toggles
    const body = Object.keys(toggles).length ? { toggles } : {};

    this.portfolioService.generateViewsPayload(this.portfolio.id, body as any).subscribe(
      (resp: any) => {
        this.payloadLoading = false;

        if (resp.error) {
          this.payloadError = resp.error;
          this.cdr.detectChanges();
          return;
        }

        const data = resp.success || resp;
        this.effectivePayload = (data.payload || []).map(item => ({
          ...item,
          resolvedName: this.resolveItemName(item.item_id, item.type),
        }));

        if (data.frequency_changed && !this.frequencyConfirmedThisSession) {
          const dialogData = new ConfirmDialogModel(
            'Frequency Change',
            `The model frequency has been changed from ${data.previous_frequency} to ${data.enforced_frequency} because a lower-frequency view was activated.`,
            true
          );
          this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '400px',
            data: dialogData,
          }).afterClosed().subscribe(() => {
            this.frequencyConfirmedThisSession = true;
          });
        }

        this.cdr.detectChanges();
      },
      (err: any) => {
        this.payloadLoading = false;
        this.payloadError = err?.error?.error || 'An unexpected error occurred.';
        this.cdr.detectChanges();
      }
    );
  }
}
